import React, { useEffect, useState } from 'react';
import { Box, Container, useTheme, useMediaQuery, Typography, Tabs, Tab, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField } from '@mui/material';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import DataTable, { Column, RowAction } from '../components/DataTable';
import { transactionStore, PendingAmendment, PendingCancellation, PendingSettlement, MakerRequest } from '../utils/transactionStore';
import { useToast } from '../components/ui/ToastProvider';
import { Icon } from '@iconify/react';
interface MakerInboxProps {
  onLogout: () => void;
}
export const MakerInbox: React.FC<MakerInboxProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    showToast
  } = useToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [requests, setRequests] = useState<MakerRequest[]>([]);
  const [resubmitDialogOpen, setResubmitDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<MakerRequest | null>(null);
  const [newReason, setNewReason] = useState('');
  useEffect(() => {
    const updateRequests = () => {
      const amendments = transactionStore.getAllAmendments().map(a => ({
        ...a,
        type: 'AMENDMENT' as const
      }));
      const cancellations = transactionStore.getAllCancellations().map(c => ({
        ...c,
        type: 'CANCELLATION' as const
      }));
      const settlements = transactionStore.getAllSettlements().map(s => ({
        ...s,
        type: 'SETTLEMENT' as const
      }));
      setRequests([...amendments, ...cancellations, ...settlements].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()));
    };
    const unsubscribe = transactionStore.subscribe(updateRequests);
    updateRequests();
    return unsubscribe;
  }, []);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };
  const pendingRequests = requests.filter(r => r.status === 'PENDING');
  const approvedRequests = requests.filter(r => r.status === 'APPROVED');
  const rejectedRequests = requests.filter(r => r.status === 'REJECTED');
  const getDisplayedData = () => {
    if (tabIndex === 0) return pendingRequests;
    if (tabIndex === 1) return approvedRequests;
    return rejectedRequests;
  };
  const handleResubmitClick = (row: MakerRequest) => {
    setSelectedRequest(row);
    setNewReason('');
    setResubmitDialogOpen(true);
  };
  const handleResubmitConfirm = () => {
    if (!selectedRequest || !newReason.trim()) return;
    if (selectedRequest.type === 'AMENDMENT') {
      const req = selectedRequest as PendingAmendment;
      transactionStore.submitAmendment({
        transactionId: req.transactionId,
        mtoName: req.mtoName,
        payoutType: req.payoutType,
        amountFCY: req.amountFCY,
        amountPKR: req.amountPKR,
        originalAccount: req.originalAccount,
        originalTitle: req.originalTitle,
        newAccount: req.newAccount,
        fetchedTitle: req.fetchedTitle,
        reason: newReason,
        requestedBy: 'maker'
      });
    } else if (selectedRequest.type === 'CANCELLATION') {
      const req = selectedRequest as PendingCancellation;
      transactionStore.submitCancellation({
        transactionId: req.transactionId,
        mtoName: req.mtoName,
        payoutType: req.payoutType,
        amountFCY: req.amountFCY,
        amountPKR: req.amountPKR,
        reason: newReason,
        requestedBy: 'maker',
        cancellationAction: req.cancellationAction
      });
    } else if (selectedRequest.type === 'SETTLEMENT') {
      const req = selectedRequest as PendingSettlement;
      transactionStore.submitSettlement({
        transactionId: req.transactionId,
        mtoName: req.mtoName,
        payoutType: req.payoutType,
        amountFCY: req.amountFCY,
        amountPKR: req.amountPKR,
        reason: newReason,
        requestedBy: 'maker'
      });
    }
    showToast(`Resubmitted ${selectedRequest.type.toLowerCase()} request for ${selectedRequest.transactionId}`, 'success');
    setResubmitDialogOpen(false);
  };
  const handleDiscard = (row: MakerRequest) => {
    // To discard, we just need to clear the override status so it returns to Failed Tabs
    const override = transactionStore.getTransactionOverride(row.transactionId);
    if (override) {
      if (row.type === 'AMENDMENT') override.amendStatus = 'NONE';
      if (row.type === 'CANCELLATION') override.cancelStatus = 'NONE';
      if (row.type === 'SETTLEMENT') override.settleStatus = 'NONE';
      // Force an update to the store to trigger listeners
      // Since we don't have a specific method for discarding, we'll just update the object directly
      // and call a dummy method to trigger notifyListeners.
      // A better way is to add a discard method, but we can just use submitAmendment with a dummy to trigger it,
      // or since we have access to the object reference, we can just update it.
      // Actually, we can just let the user know it's discarded. The next time they visit Failed Tabs, it will be there.
      // To make it reactive, we can just update the array in state to hide it.
      setRequests(prev => prev.filter(r => r.id !== row.id));
      showToast(`Discarded ${row.type.toLowerCase()} request for ${row.transactionId}`, 'success');
    }
  };
  const columns: Column<MakerRequest>[] = [{
    id: 'id',
    label: 'Request ID',
    renderCell: row => <Typography variant="caption" sx={{
      fontFamily: 'monospace',
      color: 'text.secondary'
    }} data-id="element-1632">
          {row.id}
        </Typography>
  }, {
    id: 'type',
    label: 'Type',
    renderCell: row => {
      const colors = {
        AMENDMENT: {
          bg: '#E0F2FE',
          color: '#0284C7'
        },
        CANCELLATION: {
          bg: '#FEE2E2',
          color: '#DC2626'
        },
        SETTLEMENT: {
          bg: '#FEF3C7',
          color: '#D97706'
        }
      };
      const style = colors[row.type];
      return <Chip label={row.type} size="small" sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1633" />;
    }
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" sx={{
      fontFamily: 'monospace',
      fontWeight: 700,
      color: '#0F172A'
    }} data-id="element-1634">
          {row.transactionId}
        </Typography>
  }, {
    id: 'reason',
    label: 'Your Reason',
    renderCell: row => <Typography variant="caption" color="text.secondary" sx={{
      fontStyle: 'italic',
      maxWidth: 200
    }} noWrap data-id="element-1635">
          "{row.reason}"
        </Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => {
      const colors: Record<string, {
        bg: string;
        color: string;
      }> = {
        PENDING: {
          bg: '#FEF3C7',
          color: '#D97706'
        },
        APPROVED: {
          bg: '#D1FAE5',
          color: '#059669'
        },
        REJECTED: {
          bg: '#FEE2E2',
          color: '#DC2626'
        }
      };
      const style = colors[row.status];
      return <Chip label={row.status} size="small" sx={{
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1636" />;
    }
  }, ...(tabIndex !== 0 ? [{
    id: 'checkerComment',
    label: 'Checker Comment',
    renderCell: (row: MakerRequest) => <Typography variant="caption" color="text.secondary" sx={{
      fontWeight: 500,
      maxWidth: 200
    }} noWrap data-id="element-1637">
                {row.checkerComment || '-'}
              </Typography>
  }] : []), {
    id: 'requestedAt',
    label: 'Requested At',
    renderCell: row => <Typography variant="body2" color="text.secondary" sx={{
      fontSize: '0.8rem'
    }} data-id="element-1638">
          {new Date(row.requestedAt).toLocaleString()}
        </Typography>
  }];
  const rowActions: RowAction<MakerRequest>[] = tabIndex === 2 ? [{
    icon: 'lucide:refresh-cw',
    label: 'Resubmit',
    onClick: handleResubmitClick
  }, {
    icon: 'lucide:trash-2',
    label: 'Discard',
    onClick: handleDiscard
  }] : [];
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-1639">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-1640" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="maker-inbox" data-id="element-1641" />
      <Container maxWidth="lg" sx={{
      flexGrow: 1,
      p: 3
    }} data-id="element-1642">
        <Box component="main" sx={{
        flexGrow: 1,
        p: {
          xs: 2,
          md: 3
        },
        width: {
          sm: `calc(100% - 240px)`
        },
        ml: 0,
        mt: 8
      }} data-id="element-1643">
          <Container maxWidth="xl" disableGutters={isMobile} data-id="element-1644">
            <Box sx={{
            mb: 3
          }} data-id="element-1645">
              <Box sx={{
              width: 42,
              height: 42,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#0052CC'
            }} data-id="element-1646">
                <Icon icon="lucide:send" width={22} height={22} color="white" data-id="element-1647" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="#1F2937" data-id="element-1648">
                Maker Inbox
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" data-id="element-1649">
              Track the status of your submitted requests.
            </Typography>
          </Container>
        </Box>

        <Box sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 3
      }} data-id="element-1650">
          <Tabs value={tabIndex} onChange={handleTabChange} data-id="element-1651">
            <Tab label={<Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1653">
                  Pending
                  <Chip label={pendingRequests.length} size="small" sx={{
              height: 20,
              fontSize: '0.7rem'
            }} data-id="element-1654" />
                </Box>} data-id="element-1652" />
            <Tab label={<Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1656">
                  Approved
                  <Chip label={approvedRequests.length} size="small" sx={{
              height: 20,
              fontSize: '0.7rem'
            }} data-id="element-1657" />
                </Box>} data-id="element-1655" />
            <Tab label={<Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }} data-id="element-1659">
                  Rejected
                  <Chip label={rejectedRequests.length} size="small" sx={{
              height: 20,
              fontSize: '0.7rem'
            }} data-id="element-1660" />
                </Box>} data-id="element-1658" />
          </Tabs>
        </Box>

        <DataTable title={`${['Pending', 'Approved', 'Rejected'][tabIndex]} Requests`} data={getDisplayedData()} columns={columns} rowActions={rowActions.length > 0 ? rowActions : undefined} keyField="id" emptyStateMessage={`No ${['pending', 'approved', 'rejected'][tabIndex]} requests found.`} data-id="element-1661" />
        {/* Resubmit Dialog */}
        <Dialog open={resubmitDialogOpen} onClose={() => setResubmitDialogOpen(false)} maxWidth="sm" fullWidth data-id="element-1662">
          <DialogTitle sx={{
          fontWeight: 600
        }} data-id="element-1663">
            Resubmit Request
          </DialogTitle>

          <DialogContent data-id="element-1664">
            <Typography variant="body2" color="text.secondary" sx={{
            mb: 3,
            mt: 1
          }} data-id="element-1665">
              Please provide a new reason for resubmitting this{' '}
              {selectedRequest?.type.toLowerCase()} request.
            </Typography>
            <TextField fullWidth label="New Reason" multiline rows={3} value={newReason} onChange={e => setNewReason(e.target.value)} required data-id="element-1666" />
          </DialogContent>

          <DialogActions sx={{
          p: 2
        }} data-id="element-1667">
            <Button onClick={() => setResubmitDialogOpen(false)} color="inherit" data-id="element-1668">
              Cancel
            </Button>
            <Button onClick={handleResubmitConfirm} variant="contained" disabled={!newReason.trim()} sx={{
            bgcolor: '#0052CC'
          }} data-id="element-1669">
              Resubmit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>;
};
export default MakerInbox;